import type { DataRepository } from '@/types/repository';
import type {
  Manuscript,
  Series,
  Collection,
  PlannerStructure,
  CodexEntry,
  Goals,
  FormatSettings,
  SceneContent,
  OutboxEntry,
} from '@/types/database';
import { supabase } from '@/integrations/supabase/client';
import { purgeCloudManuscript } from '@/lib/repository/purgeCloud';
import { logger } from '@/lib/logger';
import { getE2ECryptoKey } from '@/lib/cryptoKeyStore';
import {
  getE2ESetupKey,
  getLastSyncedKey,
  getLastSyncedHashKey,
  getEntityHashesKey,
} from '@/utils/storageKeys';
import { encrypt as cryptoEncrypt, generateSalt } from '@/lib/crypto';
import { gatherBackupPayload } from '@/lib/backupPayload';
import { createConflictCopy } from '@/lib/conflictCopier';
import { getSha256, stringifyCanonicalJson } from '@/utils/hash';
import { computeEntityHashes, smartMergePayloads } from '@/lib/smartMerge';
import {
  getDeviceId,
  getLastSyncedRevision,
  setLastSyncedRevision,
  getLastSyncedBaseHashes,
  setLastSyncedBaseHashes,
  commitManuscriptHeadRpc,
  decryptPayloadString,
} from '@/lib/revisionedSync';
import { withLock } from '@/utils/lock';
import {
  getAllManuscripts as dbGetAllManuscripts,
  getManuscript as dbGetManuscript,
  putManuscript as dbPutManuscript,
  deleteManuscriptCascade as dbDeleteManuscript,
  deleteManuscriptCascadeWithOutbox,
  getAllSeries as dbGetAllSeries,
  getSeries as dbGetSeries,
  getAllCollections as dbGetAllCollections,
  getCollection as dbGetCollection,
  getPlannerStructure as dbGetPlannerStructure,
  putPlannerStructure as dbPutPlannerStructure,
  getCodexEntriesForManuscript as dbGetCodexEntriesForManuscript,
  getCodexEntry as dbGetCodexEntry,
  putCodexEntry as dbPutCodexEntry,
  deleteCodexEntry as dbDeleteCodexEntry,
  getGoals as dbGetGoals,
  putGoals as dbPutGoals,
  getFormatSettings as dbGetFormatSettings,
  putFormatSettings as dbPutFormatSettings,
  getSceneContent as dbGetSceneContent,
  putSceneContent as dbPutSceneContent,
  deleteSceneContent as dbDeleteSceneContent,
  getAllSceneContentForManuscript as dbGetAllSceneContentForManuscript,
  putOutboxEntry,
  deleteOutboxEntry,
  getOutboxEntry,
  getAllOutboxEntries,
  putManuscriptWithOutbox,
  putPlannerStructureWithOutbox,
  putSceneContentWithOutbox,
  deleteSceneContentWithOutbox,
  putCodexEntryWithOutbox,
  deleteCodexEntryWithOutbox,
  putGoalsWithOutbox,
  putFormatSettingsWithOutbox,
  putSeriesWithOutbox,
  deleteSeriesWithOutbox,
  putCollectionWithOutbox,
  deleteCollectionWithOutbox,
} from '@/lib/db';

const DEBOUNCE_DELAY_MS = 2500;
const MAX_RETRY_DELAY_MS = 60_000;

export class AndroidRepository implements DataRepository {
  private saveDebounceTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  private inFlightUpsertPromises = new Map<string, Promise<void>>();
  private localGenerations = new Map<string, number>();
  private retryAttempts = new Map<string, number>();
  private isListenersInitialized = false;
  private activeUserId: string | null = null;

  constructor() {
    this.setupLifecycleListeners();
    void this.resumeForAuthenticatedUser();
  }

  private async resumeForAuthenticatedUser(): Promise<void> {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;

      if (!userId) return;

      this.activeUserId = userId;
      await this.scanAndResumeOutbox();
    } catch (err) {
      logger.log(
        '[AndroidRepository] Failed to resume authenticated outbox:',
        err
      );
    }
  }

  public async scanAndResumeOutbox(): Promise<void> {
    if (
      typeof window === 'undefined' ||
      typeof indexedDB === 'undefined'
    ) {
      return;
    }

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;

      if (!userId) return;

      this.activeUserId = userId;

      const entries = await getAllOutboxEntries();

      for (const entry of entries) {
        const existingGeneration =
          this.localGenerations.get(entry.manuscriptId) ?? 0;

        this.localGenerations.set(
          entry.manuscriptId,
          Math.max(existingGeneration, entry.generation)
        );

        if (entry.isTombstone) {
          void this.executeCloudUpsert(entry.manuscriptId);
        } else {
          this.scheduleCloudPush(entry.manuscriptId, 0);
        }
      }
    } catch (err) {
      logger.log('[AndroidRepository] Error scanning outbox:', err);
    }
  }

  private clearScheduledWrites(): void {
    for (const timer of this.saveDebounceTimers.values()) {
      clearTimeout(timer);
    }

    this.saveDebounceTimers.clear();
    this.localGenerations.clear();
    this.retryAttempts.clear();
  }

  private setupLifecycleListeners(): void {
    if (this.isListenersInitialized || typeof window === 'undefined') {
      return;
    }

    this.isListenersInitialized = true;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        logger.log(
          '[AndroidRepository] App hidden — flushing pending writes.'
        );
        void this.flushPendingWrites();
      } else {
        void this.scanAndResumeOutbox();
      }
    };

    const handleUnload = () => {
      void this.flushPendingWrites();
    };

    const handleOnline = () => {
      void this.scanAndResumeOutbox();
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('online', handleOnline);

    supabase.auth.onAuthStateChange((_event, session) => {
      const newUserId = session?.user?.id ?? null;

      if (newUserId === this.activeUserId) return;

      this.clearScheduledWrites();
      this.activeUserId = newUserId;

      if (newUserId) {
        window.setTimeout(() => {
          void this.scanAndResumeOutbox();
        }, 0);
      }
    });
  }

  public hasPendingWrites(manuscriptId?: string): boolean {
    if (manuscriptId) {
      return (
        this.saveDebounceTimers.has(manuscriptId) ||
        this.inFlightUpsertPromises.has(manuscriptId)
      );
    }

    return (
      this.saveDebounceTimers.size > 0 ||
      this.inFlightUpsertPromises.size > 0
    );
  }

  public async flushPendingWrites(
    manuscriptId?: string
  ): Promise<void> {
    const manuscriptIds = new Set<string>();

    if (manuscriptId) {
      const timer = this.saveDebounceTimers.get(manuscriptId);

      if (timer) {
        clearTimeout(timer);
        this.saveDebounceTimers.delete(manuscriptId);
      }

      manuscriptIds.add(manuscriptId);
    } else {
      for (const [currentId, timer] of this.saveDebounceTimers) {
        clearTimeout(timer);
        manuscriptIds.add(currentId);
      }

      this.saveDebounceTimers.clear();

      try {
        const entries = await getAllOutboxEntries();

        for (const entry of entries) {
          manuscriptIds.add(entry.manuscriptId);
        }
      } catch (err) {
        logger.log(
          '[AndroidRepository] Failed to enumerate the outbox while flushing:',
          err
        );
      }
    }

    const promises = Array.from(manuscriptIds).map((currentId) =>
      this.executeCloudUpsert(currentId)
    );

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  public async recordMutation(manuscriptId: string): Promise<void> {
    if (!manuscriptId) return;

    const outboxEntry = this.getNextOutboxEntry(manuscriptId);
    await putOutboxEntry(outboxEntry);
    this.scheduleCloudPush(manuscriptId);
  }

  public scheduleCloudPush(
    manuscriptId: string,
    delayMs: number = DEBOUNCE_DELAY_MS
  ): void {
    if (!manuscriptId) return;

    const existingTimer =
      this.saveDebounceTimers.get(manuscriptId);

    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.saveDebounceTimers.delete(manuscriptId);
      void this.executeCloudUpsert(manuscriptId);
    }, Math.max(0, delayMs));

    this.saveDebounceTimers.set(manuscriptId, timer);
  }

  private getRetryDelay(manuscriptId: string): number {
    const attempt = (this.retryAttempts.get(manuscriptId) ?? 0) + 1;
    this.retryAttempts.set(manuscriptId, attempt);

    return Math.min(
      MAX_RETRY_DELAY_MS,
      1000 * Math.pow(2, Math.min(attempt - 1, 6))
    );
  }

  private async deleteOutboxIfGenerationMatches(
    manuscriptId: string,
    generation: number
  ): Promise<void> {
    const current = await getOutboxEntry(manuscriptId);

    if (current?.generation === generation) {
      await deleteOutboxEntry(manuscriptId);
    }
  }

  private async commitPendingTombstone(
    entry: OutboxEntry,
    userId: string
  ): Promise<void> {
    const expectedRevision =
      entry.expectedRevision ??
      getLastSyncedRevision(entry.manuscriptId);

    const result = await commitManuscriptHeadRpc(
      entry.manuscriptId,
      expectedRevision,
      '{}',
      'tombstone',
      false,
      getDeviceId(),
      entry.deletedAt ?? entry.updatedAt
    );

    if (result.accepted) {
      if (entry.purgeCloud) {
        await purgeCloudManuscript(userId, entry.manuscriptId);
      }

      await this.deleteOutboxIfGenerationMatches(
        entry.manuscriptId,
        entry.generation
      );
      this.retryAttempts.delete(entry.manuscriptId);
      return;
    }

    const currentEntry = await getOutboxEntry(entry.manuscriptId);

    if (
      currentEntry?.isTombstone &&
      currentEntry.generation === entry.generation
    ) {
      await putOutboxEntry({
        ...currentEntry,
        expectedRevision: result.currentRevision,
        updatedAt: new Date().toISOString(),
      });
    }
  }

  public async executeCloudUpsert(
    manuscriptId: string
  ): Promise<void> {
    const inFlight =
      this.inFlightUpsertPromises.get(manuscriptId);

    if (inFlight) {
      return inFlight;
    }

    let currentUploadedGeneration =
      this.localGenerations.get(manuscriptId) ?? 0;

    const upsertPromise = (async () => {
      try {
        const pendingEntry = await getOutboxEntry(manuscriptId);

        if (pendingEntry) {
          currentUploadedGeneration = pendingEntry.generation;
          this.localGenerations.set(
            manuscriptId,
            Math.max(
              this.localGenerations.get(manuscriptId) ?? 0,
              pendingEntry.generation
            )
          );
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userId = session?.user?.id;

        if (!userId) return;

        if (pendingEntry?.isTombstone) {
          await this.commitPendingTombstone(
            pendingEntry,
            userId
          );
          return;
        }

        const payload = await gatherBackupPayload(
          manuscriptId,
          this
        );

        if (!payload.manuscript) {
          logger.log(
            `[AndroidRepository] Manuscript ${manuscriptId} was not found. Leaving any durable intent for recovery.`
          );
          return;
        }

        const payloadJson = stringifyCanonicalJson(payload);
        const payloadHash = await getSha256(payloadJson);
        const entityHashes = await computeEntityHashes(payload);

        const lastSyncedHash = localStorage.getItem(
          getLastSyncedHashKey(manuscriptId)
        );

        if (payloadHash === lastSyncedHash) {
          if (pendingEntry) {
            await this.deleteOutboxIfGenerationMatches(
              manuscriptId,
              currentUploadedGeneration
            );
          }

          this.retryAttempts.delete(manuscriptId);
          return;
        }

        const isE2E =
          localStorage.getItem(getE2ESetupKey(userId)) === 'true';

        let contentStr = payloadJson;
        let isEncrypted = false;

        if (isE2E) {
          const masterKey = getE2ECryptoKey(userId);

          if (!masterKey) {
            logger.log(
              '[AndroidRepository] E2E is enabled but the current user key is locked. The durable outbox entry is retained.'
            );
            return;
          }

          const encryptedBlob = await cryptoEncrypt(
            payloadJson,
            masterKey,
            generateSalt()
          );
          contentStr = JSON.stringify(encryptedBlob);
          isEncrypted = true;
        }

        await withLock(
          `deckle_backup_lock_${manuscriptId}`,
          async () => {
            const deviceId = getDeviceId();
            const expectedRevision =
              getLastSyncedRevision(manuscriptId);

            const commitResult =
              await commitManuscriptHeadRpc(
                manuscriptId,
                expectedRevision,
                contentStr,
                payloadHash,
                isEncrypted,
                deviceId
              );

            if (
              !commitResult.accepted &&
              (
                commitResult as {
                  currentDeletedAt?: string | null;
                }
              ).currentDeletedAt
            ) {
              try {
                const conflictCopyId =
                  await createConflictCopy(
                    manuscriptId,
                    this
                  );

                logger.log(
                  `[AndroidRepository] Remote deletion won for ${manuscriptId}. Preserved local edits as ${conflictCopyId}.`
                );
              } catch (copyErr) {
                throw new Error(
                  `Conflict copy creation failed while resolving a remote tombstone: ${
                    copyErr instanceof Error
                      ? copyErr.message
                      : String(copyErr)
                  }`
                );
              }

              await dbDeleteManuscript(manuscriptId);
              await this.deleteOutboxIfGenerationMatches(
                manuscriptId,
                currentUploadedGeneration
              );
              localStorage.removeItem(
                getLastSyncedKey(manuscriptId)
              );
              localStorage.removeItem(
                getLastSyncedHashKey(manuscriptId)
              );
              localStorage.removeItem(
                getEntityHashesKey(manuscriptId)
              );
              return;
            }

            if (
              !commitResult.accepted &&
              commitResult.currentPayload
            ) {
              logger.log(
                `[AndroidRepository] Revision conflict on ${manuscriptId}. Running smart merge.`
              );

              const remotePayload =
                await decryptPayloadString(
                  commitResult.currentPayload,
                  Boolean(commitResult.currentIsEncrypted),
                  userId
                );

              if (!remotePayload) {
                throw new Error(
                  'Unable to decrypt the remote conflict payload.'
                );
              }

              const baseHashes =
                getLastSyncedBaseHashes(manuscriptId) ?? {
                  scenes: {},
                  codex: {},
                };

              const mergeResult = await smartMergePayloads(
                payload,
                remotePayload,
                baseHashes
              );

              if (mergeResult.hasConflicts) {
                try {
                  const conflictCopyId =
                    await createConflictCopy(
                      manuscriptId,
                      this
                    );

                  logger.log(
                    `[AndroidRepository] Created conflict copy ${conflictCopyId}.`
                  );
                } catch (copyErr) {
                  throw new Error(
                    `Conflict copy creation failed: ${
                      copyErr instanceof Error
                        ? copyErr.message
                        : String(copyErr)
                    }`
                  );
                }
              }

              if (mergeResult.merged.manuscript) {
                await dbPutManuscript(
                  mergeResult.merged.manuscript
                );
              }

              if (mergeResult.merged.plannerStructure) {
                await dbPutPlannerStructure(
                  mergeResult.merged.plannerStructure
                );
              }

              for (const scene of mergeResult.merged.sceneContent) {
                await dbPutSceneContent(scene);
              }

              for (const entry of mergeResult.merged.codexEntries) {
                await dbPutCodexEntry(entry);
              }

              if (mergeResult.merged.goals) {
                await dbPutGoals(mergeResult.merged.goals);
              }

              if (mergeResult.merged.formatSettings) {
                await dbPutFormatSettings(
                  mergeResult.merged.formatSettings
                );
              }

              const mergedJson = stringifyCanonicalJson(
                mergeResult.merged
              );
              const mergedHash = await getSha256(mergedJson);

              let retryContentStr = mergedJson;
              let retryIsEncrypted = false;

              if (isE2E) {
                const masterKey = getE2ECryptoKey(userId);

                if (!masterKey) {
                  throw new Error(
                    'The encryption key became unavailable during conflict resolution.'
                  );
                }

                const encryptedBlob = await cryptoEncrypt(
                  mergedJson,
                  masterKey,
                  generateSalt()
                );
                retryContentStr = JSON.stringify(encryptedBlob);
                retryIsEncrypted = true;
              }

              const retryResult =
                await commitManuscriptHeadRpc(
                  manuscriptId,
                  commitResult.currentRevision,
                  retryContentStr,
                  mergedHash,
                  retryIsEncrypted,
                  deviceId
                );

              if (!retryResult.accepted) {
                return;
              }

              const newBaseHashes =
                await computeEntityHashes(
                  mergeResult.merged
                );

              setLastSyncedRevision(
                manuscriptId,
                retryResult.currentRevision
              );
              setLastSyncedBaseHashes(
                manuscriptId,
                newBaseHashes
              );

              const serverUpdatedAt =
                retryResult.currentUpdatedAt ??
                new Date().toISOString();

              localStorage.setItem(
                getLastSyncedKey(manuscriptId),
                serverUpdatedAt
              );
              localStorage.setItem(
                getLastSyncedHashKey(manuscriptId),
                mergedHash
              );
              localStorage.setItem(
                getEntityHashesKey(manuscriptId),
                JSON.stringify(newBaseHashes)
              );

              await this.deleteOutboxIfGenerationMatches(
                manuscriptId,
                currentUploadedGeneration
              );
              this.retryAttempts.delete(manuscriptId);

              logger.log(
                `[AndroidRepository] Committed merged manuscript ${manuscriptId} at revision ${retryResult.currentRevision}.`
              );
              return;
            }

            if (!commitResult.accepted) {
              if (commitResult.currentRevision === 0) {
                setLastSyncedRevision(manuscriptId, 0);
              }

              return;
            }

            setLastSyncedRevision(
              manuscriptId,
              commitResult.currentRevision
            );
            setLastSyncedBaseHashes(
              manuscriptId,
              entityHashes
            );

            const serverUpdatedAt =
              commitResult.currentUpdatedAt ??
              new Date().toISOString();

            localStorage.setItem(
              getLastSyncedKey(manuscriptId),
              serverUpdatedAt
            );
            localStorage.setItem(
              getLastSyncedHashKey(manuscriptId),
              payloadHash
            );
            localStorage.setItem(
              getEntityHashesKey(manuscriptId),
              JSON.stringify(entityHashes)
            );

            await this.deleteOutboxIfGenerationMatches(
              manuscriptId,
              currentUploadedGeneration
            );
            this.retryAttempts.delete(manuscriptId);

            logger.log(
              `[AndroidRepository] Committed manuscript ${manuscriptId} at revision ${commitResult.currentRevision}.`
            );
          }
        );
      } catch (err) {
        console.error(
          `[AndroidRepository] Failed cloud upsert for ${manuscriptId}:`,
          err
        );
      } finally {
        this.inFlightUpsertPromises.delete(manuscriptId);

        try {
          const latestEntry =
            await getOutboxEntry(manuscriptId);

          if (latestEntry) {
            this.localGenerations.set(
              manuscriptId,
              Math.max(
                this.localGenerations.get(manuscriptId) ?? 0,
                latestEntry.generation
              )
            );

            const editedDuringUpload =
              latestEntry.generation >
              currentUploadedGeneration;

            this.scheduleCloudPush(
              manuscriptId,
              editedDuringUpload
                ? DEBOUNCE_DELAY_MS
                : this.getRetryDelay(manuscriptId)
            );
          } else {
            this.retryAttempts.delete(manuscriptId);
          }
        } catch (err) {
          logger.log(
            `[AndroidRepository] Failed to inspect the outbox after uploading ${manuscriptId}:`,
            err
          );
        }
      }
    })();

    this.inFlightUpsertPromises.set(
      manuscriptId,
      upsertPromise
    );

    return upsertPromise;
  }

  private getNextOutboxEntry(
    manuscriptId: string
  ): OutboxEntry {
    const nextGeneration =
      (this.localGenerations.get(manuscriptId) ?? 0) + 1;

    this.localGenerations.set(
      manuscriptId,
      nextGeneration
    );

    return {
      id: manuscriptId,
      manuscriptId,
      generation: nextGeneration,
      updatedAt: new Date().toISOString(),
    };
  }

  async getAllManuscripts(): Promise<Manuscript[]> {
    return dbGetAllManuscripts();
  }

  async getManuscript(
    id: string
  ): Promise<Manuscript | undefined> {
    return dbGetManuscript(id);
  }

  async putManuscript(
    manuscript: Manuscript
  ): Promise<void> {
    const outboxEntry = this.getNextOutboxEntry(
      manuscript.id
    );

    await putManuscriptWithOutbox(
      manuscript,
      outboxEntry
    );
    this.scheduleCloudPush(manuscript.id);
  }

  async deleteManuscript(
    id: string,
    purgeCloud: boolean = true
  ): Promise<void> {
    const existingInFlight =
      this.inFlightUpsertPromises.get(id);

    const deletedAt = new Date().toISOString();
    const outboxEntry: OutboxEntry = {
      ...this.getNextOutboxEntry(id),
      updatedAt: deletedAt,
      isTombstone: true,
      deletedAt,
      expectedRevision: getLastSyncedRevision(id),
      purgeCloud,
    };

    const timer = this.saveDebounceTimers.get(id);

    if (timer) {
      clearTimeout(timer);
      this.saveDebounceTimers.delete(id);
    }

    await deleteManuscriptCascadeWithOutbox(
      id,
      outboxEntry
    );

    localStorage.removeItem(getLastSyncedKey(id));
    localStorage.removeItem(getLastSyncedHashKey(id));
    localStorage.removeItem(getEntityHashesKey(id));

    if (existingInFlight) {
      await existingInFlight;
    }

    await this.executeCloudUpsert(id);
  }

  public async applyRemoteTombstone(
    id: string
  ): Promise<void> {
    const timer = this.saveDebounceTimers.get(id);

    if (timer) {
      clearTimeout(timer);
      this.saveDebounceTimers.delete(id);
    }

    const inFlight =
      this.inFlightUpsertPromises.get(id);

    if (inFlight) {
      await inFlight;
    }

    await dbDeleteManuscript(id);
    await deleteOutboxEntry(id);

    this.localGenerations.delete(id);
    this.retryAttempts.delete(id);

    localStorage.removeItem(getLastSyncedKey(id));
    localStorage.removeItem(getLastSyncedHashKey(id));
    localStorage.removeItem(getEntityHashesKey(id));
  }

  async getAllSeries(): Promise<Series[]> {
    return dbGetAllSeries();
  }

  async getSeries(id: string): Promise<Series | undefined> {
    return dbGetSeries(id);
  }

  async putSeries(series: Series): Promise<void> {
    const existing = await dbGetSeries(series.id);
    const manuscriptIds = new Set([
      ...(existing?.manuscripts ?? []),
      ...(series.manuscripts ?? []),
    ]);
    const entries = Array.from(manuscriptIds).map(
      (manuscriptId) =>
        this.getNextOutboxEntry(manuscriptId)
    );

    await putSeriesWithOutbox(series, entries);

    for (const manuscriptId of manuscriptIds) {
      this.scheduleCloudPush(manuscriptId);
    }
  }

  async deleteSeries(id: string): Promise<void> {
    const existing = await dbGetSeries(id);
    const manuscriptIds = existing?.manuscripts ?? [];
    const entries = manuscriptIds.map((manuscriptId) =>
      this.getNextOutboxEntry(manuscriptId)
    );

    await deleteSeriesWithOutbox(id, entries);

    for (const manuscriptId of manuscriptIds) {
      this.scheduleCloudPush(manuscriptId);
    }
  }

  async getAllCollections(): Promise<Collection[]> {
    return dbGetAllCollections();
  }

  async getCollection(
    id: string
  ): Promise<Collection | undefined> {
    return dbGetCollection(id);
  }

  async putCollection(
    collection: Collection
  ): Promise<void> {
    const existing = await dbGetCollection(collection.id);
    const manuscriptIds = new Set([
      ...(existing?.manuscripts ?? []),
      ...(collection.manuscripts ?? []),
    ]);
    const entries = Array.from(manuscriptIds).map(
      (manuscriptId) =>
        this.getNextOutboxEntry(manuscriptId)
    );

    await putCollectionWithOutbox(
      collection,
      entries
    );

    for (const manuscriptId of manuscriptIds) {
      this.scheduleCloudPush(manuscriptId);
    }
  }

  async deleteCollection(id: string): Promise<void> {
    const existing = await dbGetCollection(id);
    const manuscriptIds = existing?.manuscripts ?? [];
    const entries = manuscriptIds.map((manuscriptId) =>
      this.getNextOutboxEntry(manuscriptId)
    );

    await deleteCollectionWithOutbox(id, entries);

    for (const manuscriptId of manuscriptIds) {
      this.scheduleCloudPush(manuscriptId);
    }
  }

  async getPlannerStructure(
    manuscriptId: string
  ): Promise<PlannerStructure | undefined> {
    return dbGetPlannerStructure(manuscriptId);
  }

  async putPlannerStructure(
    structure: PlannerStructure
  ): Promise<void> {
    const outboxEntry = this.getNextOutboxEntry(
      structure.id
    );

    await putPlannerStructureWithOutbox(
      structure,
      outboxEntry
    );
    this.scheduleCloudPush(structure.id);
  }

  async getCodexEntriesForManuscript(
    manuscriptId: string
  ): Promise<CodexEntry[]> {
    return dbGetCodexEntriesForManuscript(
      manuscriptId
    );
  }

  async getCodexEntry(
    id: string
  ): Promise<CodexEntry | undefined> {
    return dbGetCodexEntry(id);
  }

  async putCodexEntry(entry: CodexEntry): Promise<void> {
    const outboxEntry = this.getNextOutboxEntry(
      entry.manuscriptId
    );

    await putCodexEntryWithOutbox(
      entry,
      outboxEntry
    );
    this.scheduleCloudPush(entry.manuscriptId);
  }

  async deleteCodexEntry(id: string): Promise<void> {
    const entry = await dbGetCodexEntry(id);

    if (!entry) {
      await dbDeleteCodexEntry(id);
      return;
    }

    const outboxEntry = this.getNextOutboxEntry(
      entry.manuscriptId
    );

    await deleteCodexEntryWithOutbox(
      id,
      outboxEntry
    );
    this.scheduleCloudPush(entry.manuscriptId);
  }

  async getGoals(
    manuscriptId: string
  ): Promise<Goals | undefined> {
    return dbGetGoals(manuscriptId);
  }

  async putGoals(goals: Goals): Promise<void> {
    const outboxEntry = this.getNextOutboxEntry(
      goals.id
    );

    await putGoalsWithOutbox(goals, outboxEntry);
    this.scheduleCloudPush(goals.id);
  }

  async getFormatSettings(
    manuscriptId: string
  ): Promise<FormatSettings | undefined> {
    return dbGetFormatSettings(manuscriptId);
  }

  async putFormatSettings(
    settings: FormatSettings
  ): Promise<void> {
    const outboxEntry = this.getNextOutboxEntry(
      settings.id
    );

    await putFormatSettingsWithOutbox(
      settings,
      outboxEntry
    );
    this.scheduleCloudPush(settings.id);
  }

  async getSceneContent(
    sceneId: string
  ): Promise<SceneContent | undefined> {
    return dbGetSceneContent(sceneId);
  }

  async putSceneContent(
    entry: SceneContent
  ): Promise<void> {
    const outboxEntry = this.getNextOutboxEntry(
      entry.manuscriptId
    );

    await putSceneContentWithOutbox(
      entry,
      outboxEntry
    );
    this.scheduleCloudPush(entry.manuscriptId);
  }

  async deleteSceneContent(
    sceneId: string
  ): Promise<void> {
    const scene = await dbGetSceneContent(sceneId);

    if (!scene) {
      await dbDeleteSceneContent(sceneId);
      return;
    }

    const outboxEntry = this.getNextOutboxEntry(
      scene.manuscriptId
    );

    await deleteSceneContentWithOutbox(
      sceneId,
      outboxEntry
    );
    this.scheduleCloudPush(scene.manuscriptId);
  }

  async getAllSceneContentForManuscript(
    manuscriptId: string
  ): Promise<SceneContent[]> {
    return dbGetAllSceneContentForManuscript(
      manuscriptId
    );
  }
}

export const androidRepository =
  new AndroidRepository();
